import { findLastIndex, repeat } from "./helpers.js";
import parse_mask from "./parse-mask.js";
class MaskUtils {
    constructor(options){
        this.isCharacterAllowedAtPosition = (character, position)=>{
            const { maskPlaceholder } = this.maskOptions;
            if (this.isCharacterFillingPosition(character, position)) return true;
            if (!maskPlaceholder) return false;
            return maskPlaceholder[position] === character;
        };
        this.isCharacterFillingPosition = (character, position)=>{
            const { mask } = this.maskOptions;
            if (!character || !mask || position >= mask.length) return false;
            if (!this.isPositionEditable(position)) return mask[position] === character;
            const charRule = mask[position];
            if ('string' == typeof charRule) return charRule === character;
            return charRule.test(character);
        };
        this.isPositionEditable = (position)=>{
            const { mask, permanents } = this.maskOptions;
            return mask ? position < mask.length && -1 === permanents.indexOf(position) : false;
        };
        this.isValueEmpty = (value)=>value.split('').every((character, position)=>!this.isPositionEditable(position) || !this.isCharacterFillingPosition(character, position));
        this.isValueFilled = (value)=>{
            const { lastEditablePosition } = this.maskOptions;
            return null !== lastEditablePosition && this.getFilledLength(value) === lastEditablePosition + 1;
        };
        this.getDefaultSelectionForValue = (value)=>{
            const filledLength = this.getFilledLength(value);
            const cursorPosition = this.getRightEditablePosition(filledLength);
            return {
                start: null != cursorPosition ? cursorPosition : 0,
                end: null != cursorPosition ? cursorPosition : 0
            };
        };
        this.getFilledLength = (value)=>{
            const characters = value.split('');
            const lastFilledIndex = findLastIndex(characters, (character, position)=>this.isPositionEditable(position) && this.isCharacterFillingPosition(character, position));
            return lastFilledIndex + 1;
        };
        this.getStringFillingLengthAtPosition = (string, position)=>{
            const characters = string.split('');
            const insertedValue = characters.reduce((value, character)=>this.insertCharacterAtPosition(value, character, value.length), repeat(' ', position));
            return insertedValue.length - position;
        };
        this.getLeftEditablePosition = (position)=>{
            for(let i = position; i >= 0; i--)if (this.isPositionEditable(i)) return i;
            return null;
        };
        this.getRightEditablePosition = (position)=>{
            const { mask } = this.maskOptions;
            if (!mask) return null;
            for(let i = position; i < mask.length; i++)if (this.isPositionEditable(i)) return i;
            return null;
        };
        this.formatValue = (value)=>{
            const { maskPlaceholder, mask } = this.maskOptions;
            if (!mask) return value;
            if (!maskPlaceholder) {
                value = this.insertStringAtPosition('', value, 0);
                while(value.length < mask.length && !this.isPositionEditable(value.length))value += mask[value.length];
                return value;
            }
            return this.insertStringAtPosition(maskPlaceholder, value, 0);
        };
        this.clearRange = (value, start, len)=>{
            if (!len) return value;
            const end = start + len;
            const { maskPlaceholder, mask } = this.maskOptions;
            if (!mask) return value;
            const clearedValue = value.split('').map((character, i)=>{
                const isEditable = this.isPositionEditable(i);
                if (!maskPlaceholder && i >= end && !isEditable) return '';
                if (i < start || i >= end) return character;
                if (!isEditable) return mask[i];
                if (maskPlaceholder) return maskPlaceholder[i];
                return '';
            }).join('');
            return this.formatValue(clearedValue);
        };
        this.insertCharacterAtPosition = (value, character, position)=>{
            const { mask, maskPlaceholder } = this.maskOptions;
            if (!mask || position >= mask.length) return value;
            const isAllowed = this.isCharacterAllowedAtPosition(character, position);
            const isEditable = this.isPositionEditable(position);
            const nextEditablePosition = this.getRightEditablePosition(position);
            const isNextPlaceholder = maskPlaceholder && nextEditablePosition ? character === maskPlaceholder[nextEditablePosition] : null;
            const valueBefore = value.slice(0, position);
            if (isAllowed || !isEditable) {
                const insertedCharacter = isAllowed ? character : mask[position];
                value = valueBefore + insertedCharacter;
            }
            if (!isAllowed && !isEditable && !isNextPlaceholder) value = this.insertCharacterAtPosition(value, character, position + 1);
            return value;
        };
        this.insertStringAtPosition = (value, string, position)=>{
            const { mask, maskPlaceholder } = this.maskOptions;
            if (!mask || !string || position >= mask.length) return value;
            const characters = string.split('');
            const isFixedLength = this.isValueFilled(value) || !!maskPlaceholder;
            const valueAfter = value.slice(position);
            value = characters.reduce((val, character)=>this.insertCharacterAtPosition(val, character, val.length), value.slice(0, position));
            if (isFixedLength) value += valueAfter.slice(value.length - position);
            else if (this.isValueFilled(value)) value += mask.slice(value.length).join('');
            else {
                const editableCharactersAfter = valueAfter.split('').filter((character, i)=>this.isPositionEditable(position + i));
                value = editableCharactersAfter.reduce((val, character)=>{
                    const nextEditablePosition = this.getRightEditablePosition(val.length);
                    if (null === nextEditablePosition) return val;
                    if (!this.isPositionEditable(val.length)) val += mask.slice(val.length, nextEditablePosition).join('');
                    return this.insertCharacterAtPosition(val, character, val.length);
                }, value);
            }
            return value;
        };
        this.processChange = (currentState, previousState)=>{
            var _previousSelection_start, _selection_start, _selection_end, _previousSelection_start1, _ref;
            const { mask, prefix, lastEditablePosition } = this.maskOptions;
            const { value, selection } = currentState;
            const previousValue = previousState.value;
            const previousSelection = previousState.selection;
            let newValue = value;
            let enteredString = '';
            let formattedEnteredStringLength = 0;
            let removedLength = 0;
            let cursorPosition = Math.min(null != (_previousSelection_start = previousSelection.start) ? _previousSelection_start : 0, null != (_selection_start = selection.start) ? _selection_start : 0);
            if ((null != (_selection_end = selection.end) ? _selection_end : 0) > (null != (_previousSelection_start1 = previousSelection.start) ? _previousSelection_start1 : 0)) {
                var _previousSelection_start2, _selection_end1;
                enteredString = newValue.slice(null != (_previousSelection_start2 = previousSelection.start) ? _previousSelection_start2 : 0, null != (_selection_end1 = selection.end) ? _selection_end1 : 0);
                formattedEnteredStringLength = this.getStringFillingLengthAtPosition(enteredString, cursorPosition);
                if (formattedEnteredStringLength) {
                    var _previousSelection_length;
                    removedLength = null != (_previousSelection_length = previousSelection.length) ? _previousSelection_length : 0;
                } else removedLength = 0;
            } else if (newValue.length < previousValue.length) removedLength = previousValue.length - newValue.length;
            newValue = previousValue;
            if (removedLength) {
                var _previousSelection_length1;
                if (1 === removedLength && !(null != (_previousSelection_length1 = previousSelection.length) ? _previousSelection_length1 : 0)) {
                    var _previousSelection_start3, _selection_start1, _this_getRightEditablePosition, _selection_start2, _this_getLeftEditablePosition, _selection_start3;
                    const deleteFromRight = (null != (_previousSelection_start3 = previousSelection.start) ? _previousSelection_start3 : 0) === (null != (_selection_start1 = selection.start) ? _selection_start1 : 0);
                    cursorPosition = deleteFromRight ? null != (_this_getRightEditablePosition = this.getRightEditablePosition(null != (_selection_start2 = selection.start) ? _selection_start2 : 0)) ? _this_getRightEditablePosition : 0 : null != (_this_getLeftEditablePosition = this.getLeftEditablePosition(null != (_selection_start3 = selection.start) ? _selection_start3 : 0)) ? _this_getLeftEditablePosition : 0;
                }
                newValue = this.clearRange(newValue, cursorPosition, removedLength);
            }
            newValue = this.insertStringAtPosition(newValue, enteredString, cursorPosition);
            cursorPosition += formattedEnteredStringLength;
            if (mask && cursorPosition >= mask.length) cursorPosition = mask.length;
            else if (prefix && cursorPosition < prefix.length && !formattedEnteredStringLength) cursorPosition = prefix.length;
            else if (null !== lastEditablePosition && cursorPosition >= (null != (_ref = null == prefix ? void 0 : prefix.length) ? _ref : 0) && cursorPosition < lastEditablePosition && formattedEnteredStringLength) {
                var _this_getRightEditablePosition1;
                cursorPosition = null != (_this_getRightEditablePosition1 = this.getRightEditablePosition(cursorPosition)) ? _this_getRightEditablePosition1 : cursorPosition;
            }
            newValue = this.formatValue(newValue);
            return {
                value: newValue,
                enteredString,
                selection: {
                    start: cursorPosition,
                    end: cursorPosition
                }
            };
        };
        this.maskOptions = parse_mask(options);
    }
}
export default MaskUtils;
